import { XIcon } from 'lucide-react'
import {
  CONDITION_CMPS,
  CONDITION_FNS,
  CONDITION_WINDOWS,
  EXPR_ALIAS_RE,
  EXPR_SOURCE_FNS,
  conditionSupportsDeadband,
  describeCondition,
  isNumericTagType,
  type AggConditionLeaf,
  type AlarmActiveConditionLeaf,
  type Condition,
  type ConditionAnd,
  type ConditionCmp,
  type ConditionFn,
  type ConditionLeaf,
  type ConditionNot,
  type ConditionOr,
  type ConditionWindow,
  type DeviationConditionLeaf,
  type ExprConditionLeaf,
  type ExprConditionSource,
  type ExprSourceFn,
  type LiveConditionLeaf
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { cn } from '../../lib/utils'
import { RegistryTagPicker } from './RegistryTagPicker'
import { canAddExprSource, defaultLeaf, nextExprAlias } from './condition-defaults'
import { isUnresolvedTagRef } from './condition-tag-ids'
import {
  cmpsForLiveKind,
  coerceCmpForKind,
  defaultValueForKind,
  liveValueKind,
  parseLiveInput
} from './live-value'

type LeafKind = ConditionLeaf['kind']

const LEAF_KIND_LABELS: Record<LeafKind, string> = {
  live: 'Live value',
  agg: 'Aggregate',
  deviation: 'Deviation',
  alarm_active: 'Alarm active',
  expr: 'Expression'
}

type ValueConditionLeaf = LiveConditionLeaf | AggConditionLeaf | DeviationConditionLeaf

function isValueLeaf(leaf: ConditionLeaf): leaf is ValueConditionLeaf {
  return leaf.kind === 'live' || leaf.kind === 'agg' || leaf.kind === 'deviation'
}

const CMP_LABELS: Record<ConditionCmp, string> = {
  gt: 'is greater than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  eq: 'equals',
  ne: 'does not equal'
}

const FN_LABELS: Record<ConditionFn, string> = {
  avg: 'average',
  min: 'minimum',
  max: 'maximum',
  last: 'last value',
  stddev: 'standard deviation',
  delta: 'delta',
  sum: 'sum',
  count: 'count'
}

const WINDOW_LABELS: Record<ConditionWindow, string> = {
  '1m': 'last 1 minute',
  '5m': 'last 5 minutes',
  '15m': 'last 15 minutes',
  '1h': 'last 1 hour',
  '6h': 'last 6 hours',
  '24h': 'last 24 hours'
}

const UNRESOLVED_TAG_HINT =
  'This tag is no longer in the registry — pick a replacement before saving.'

/** Remove a node's `description` key (an empty description is "use the generated sentence"). */
export function withoutDescription<T extends Condition>(node: T): T {
  if (node.description === undefined) return node
  const { description: _dropped, ...rest } = node
  return rest as T
}

/**
 * Optional per-node description. Builder agents author prose like
 * "ProcessedCount delta … is zero or negative" on each node and the read view
 * prefers it over the generated sentence, so operators need a way to fix it
 * after changing a threshold. Empty = fall back to `describeCondition`, which
 * is shown as the placeholder. Authored text is never cleared automatically.
 */
function DescriptionField({
  node,
  onChange
}: {
  node: Condition
  onChange: (next: Condition) => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs">Description (optional)</Label>
      <Input
        aria-label="Condition description"
        value={node.description ?? ''}
        onChange={(e) => {
          const value = e.target.value
          onChange(value.trim() === '' ? withoutDescription(node) : { ...node, description: value })
        }}
        placeholder={describeCondition(node) || 'How this condition reads to an operator'}
        className="h-8 text-xs"
      />
    </div>
  )
}

/**
 * Windows are open-ended Go durations on the wire; agent-authored conditions
 * often use values (e.g. `10m`, `30m`) outside the dropdown presets. Offer the
 * stored value as an extra option so the select shows it instead of blank
 * and editing another field does not silently clobber it.
 */
function windowOptions(current: string): Array<{ value: string; label: string }> {
  const presets = CONDITION_WINDOWS.map((w) => ({ value: w, label: WINDOW_LABELS[w] }))
  const trimmed = current.trim()
  if (!trimmed || (CONDITION_WINDOWS as readonly string[]).includes(trimmed)) return presets
  return [{ value: trimmed, label: `last ${trimmed}` }, ...presets]
}

function defaultAnd(): ConditionAnd {
  return { kind: 'and', operands: [defaultLeaf('live')] }
}

function defaultOr(): ConditionOr {
  return { kind: 'or', operands: [defaultLeaf('live'), defaultLeaf('live')] }
}

function defaultNot(): ConditionNot {
  return { kind: 'not', operand: defaultLeaf('live') }
}

function lookupTagType(
  tagId: string,
  tagTypesById: Record<string, string | undefined> | undefined
): string | undefined {
  if (!tagTypesById) return undefined
  return tagTypesById[tagId]
}

function canUseAggregates(
  tagId: string,
  tagTypesById: Record<string, string | undefined> | undefined
): boolean {
  const t = lookupTagType(tagId, tagTypesById)
  if (t === undefined || t === '') return true
  return isNumericTagType(t)
}

export interface ConditionBuilderProps {
  condition: Condition
  onChange: (next: Condition) => void
  onRemove?: () => void
  /**
   * When set, leaf RegistryTagPickers only offer these registry ids (e.g. the
   * form's selected tag set). Omit / empty = browse the full registry.
   */
  availableTagIds?: readonly string[]
  /** Optional `tag_id → Ignition data type` map gating agg/deviation leaves. */
  tagTypesById?: Record<string, string | undefined>
  depth?: number
}

export function ConditionBuilder({
  condition,
  onChange,
  onRemove,
  availableTagIds,
  tagTypesById,
  depth = 0
}: ConditionBuilderProps) {
  if (condition.kind === 'and' || condition.kind === 'or') {
    return (
      <GroupNode
        condition={condition}
        depth={depth}
        availableTagIds={availableTagIds}
        tagTypesById={tagTypesById}
        onChange={onChange}
        onRemove={onRemove}
      />
    )
  }
  if (condition.kind === 'not') {
    return (
      <NotNode
        condition={condition}
        depth={depth}
        availableTagIds={availableTagIds}
        tagTypesById={tagTypesById}
        onChange={onChange}
        onRemove={onRemove}
      />
    )
  }
  return (
    <LeafEditor
      leaf={condition}
      onChange={onChange}
      onRemove={onRemove}
      availableTagIds={availableTagIds}
      tagTypesById={tagTypesById}
    />
  )
}

function GroupNode({
  condition,
  depth,
  availableTagIds,
  tagTypesById,
  onChange,
  onRemove
}: {
  condition: ConditionAnd | ConditionOr
  depth: number
  availableTagIds?: readonly string[]
  tagTypesById?: Record<string, string | undefined>
  onChange: (next: Condition) => void
  onRemove?: () => void
}) {
  const isAnd = condition.kind === 'and'
  const appendOperand = (operand: Condition) =>
    onChange({ ...condition, operands: [...condition.operands, operand] })
  return (
    <div className={cn('flex flex-col gap-2', depth > 0 && 'border-l-2 border-line pl-3')}>
      <div className="flex flex-row items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium',
            isAnd ? 'bg-primary/10 text-primary' : 'bg-st-warn-bg text-st-warn'
          )}
        >
          {isAnd ? 'All of' : 'Any of'}
        </span>
        <Select
          value={condition.kind}
          onValueChange={(val) => {
            if (!val || val === condition.kind) return
            if (val === 'and')
              onChange({ kind: 'and', operands: condition.operands } satisfies ConditionAnd)
            else if (val === 'or')
              onChange({ kind: 'or', operands: condition.operands } satisfies ConditionOr)
            else if (val === 'not')
              onChange({ kind: 'not', operand: condition.operands[0] ?? defaultLeaf('live') })
          }}
        >
          <SelectTrigger className="h-7 w-32 px-2 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="and">All of (AND)</SelectItem>
            <SelectItem value="or">Any of (OR)</SelectItem>
            <SelectItem value="not">None of (NOT)</SelectItem>
          </SelectContent>
        </Select>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onRemove}
            aria-label="Remove group"
          >
            <XIcon className="size-4" />
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {condition.operands.map((op, i) => (
          <ConditionBuilder
            key={i}
            condition={op}
            depth={depth + 1}
            availableTagIds={availableTagIds}
            tagTypesById={tagTypesById}
            onChange={(next) => {
              const operands = condition.operands.slice()
              operands[i] = next
              onChange({ ...condition, operands })
            }}
            onRemove={
              condition.operands.length > 1
                ? () => {
                    const operands = condition.operands.filter((_, j) => j !== i)
                    onChange({ ...condition, operands })
                  }
                : undefined
            }
          />
        ))}
      </div>
      <div className="flex flex-row flex-wrap gap-1.5">
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={() => appendOperand(defaultLeaf('live'))}
        >
          + Add condition
        </Button>
        <Button type="button" variant="ghost" size="xs" onClick={() => appendOperand(defaultAnd())}>
          + All-of group
        </Button>
        <Button type="button" variant="ghost" size="xs" onClick={() => appendOperand(defaultOr())}>
          + Any-of group
        </Button>
        <Button type="button" variant="ghost" size="xs" onClick={() => appendOperand(defaultNot())}>
          + Negated group
        </Button>
      </div>
      <DescriptionField node={condition} onChange={onChange} />
    </div>
  )
}

function NotNode({
  condition,
  depth,
  availableTagIds,
  tagTypesById,
  onChange,
  onRemove
}: {
  condition: ConditionNot
  depth: number
  availableTagIds?: readonly string[]
  tagTypesById?: Record<string, string | undefined>
  onChange: (next: Condition) => void
  onRemove?: () => void
}) {
  return (
    <div className={cn('flex flex-col gap-2', depth > 0 && 'border-l-2 border-st-down/40 pl-3')}>
      <div className="flex flex-row items-center gap-2">
        <span className="inline-flex items-center rounded bg-st-down-bg px-2 py-0.5 text-xs font-medium text-st-down">
          None of (NOT)
        </span>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onRemove}
            aria-label="Remove negation"
          >
            <XIcon className="size-4" />
          </Button>
        )}
      </div>
      <ConditionBuilder
        condition={condition.operand}
        depth={depth + 1}
        availableTagIds={availableTagIds}
        tagTypesById={tagTypesById}
        onChange={(next) => onChange({ ...condition, operand: next })}
      />
      <DescriptionField node={condition} onChange={onChange} />
    </div>
  )
}

function LeafEditor({
  leaf,
  onChange,
  onRemove,
  availableTagIds,
  tagTypesById
}: {
  leaf: ConditionLeaf
  onChange: (next: Condition) => void
  onRemove?: () => void
  availableTagIds?: readonly string[]
  tagTypesById?: Record<string, string | undefined>
}) {
  if (leaf.kind === 'expr') {
    return (
      <ExprLeafEditor
        leaf={leaf}
        onChange={onChange}
        onRemove={onRemove}
        availableTagIds={availableTagIds}
        tagTypesById={tagTypesById}
      />
    )
  }
  const tagType = lookupTagType(leaf.tag_id, tagTypesById)
  const aggregatesAllowed = canUseAggregates(leaf.tag_id, tagTypesById)
  const lockedToLive = !aggregatesAllowed && (leaf.kind === 'agg' || leaf.kind === 'deviation')

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-bg-elev/40 p-3">
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="flex flex-row items-center gap-2">
          <span className="text-xs font-medium text-ink-3">When</span>
          <Select
            value={leaf.kind}
            onValueChange={(val) => {
              if (!val) return
              if ((val === 'agg' || val === 'deviation') && !aggregatesAllowed) return
              if (
                val === 'live' ||
                val === 'agg' ||
                val === 'deviation' ||
                val === 'alarm_active' ||
                val === 'expr'
              ) {
                const base = defaultLeaf(val)
                if (val === 'expr') {
                  onChange(base)
                  return
                }
                if (val === 'alarm_active') {
                  onChange({
                    ...base,
                    tag_id: leaf.tag_id,
                    tag_path: leaf.tag_path,
                    alarm_name: leaf.kind === 'alarm_active' ? leaf.alarm_name : ''
                  } as AlarmActiveConditionLeaf)
                  return
                }
                onChange({
                  ...base,
                  tag_id: leaf.tag_id,
                  tag_path: leaf.tag_path,
                  ...(isValueLeaf(leaf) ? { cmp: leaf.cmp, value: leaf.value } : {})
                } as Condition)
              }
            }}
          >
            <SelectTrigger className="h-7 w-36 px-2 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="live">{LEAF_KIND_LABELS.live}</SelectItem>
              <SelectItem value="agg" disabled={!aggregatesAllowed}>
                {LEAF_KIND_LABELS.agg}
                {!aggregatesAllowed ? ' (numeric only)' : ''}
              </SelectItem>
              <SelectItem value="deviation" disabled={!aggregatesAllowed}>
                {LEAF_KIND_LABELS.deviation}
                {!aggregatesAllowed ? ' (numeric only)' : ''}
              </SelectItem>
              <SelectItem value="alarm_active">{LEAF_KIND_LABELS.alarm_active}</SelectItem>
              <SelectItem value="expr">{LEAF_KIND_LABELS.expr}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onRemove}
            aria-label="Remove condition"
          >
            <XIcon className="size-4" />
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">
            Tag
            {tagType && (
              <span
                className={cn(
                  'ml-1.5 inline-flex items-center rounded px-1.5 py-0 text-10',
                  aggregatesAllowed ? 'bg-bg-sunk text-ink-3' : 'bg-st-down-bg text-st-down'
                )}
              >
                {tagType}
              </span>
            )}
          </Label>
          <RegistryTagPicker
            value={leaf.tag_id}
            selectedTagPath={leaf.tag_path}
            availableTagIds={availableTagIds}
            invalid={isUnresolvedTagRef(leaf)}
            onChange={(tagId, tagPath) => {
              const nextAllowsAgg = canUseAggregates(tagId, tagTypesById)
              const nextType = lookupTagType(tagId, tagTypesById)
              if ((leaf.kind === 'agg' || leaf.kind === 'deviation') && !nextAllowsAgg) {
                const kind = liveValueKind(nextType, leaf.value)
                onChange({
                  ...defaultLeaf('live'),
                  tag_id: tagId,
                  tag_path: tagPath,
                  cmp: coerceCmpForKind(leaf.cmp, kind),
                  value: defaultValueForKind(kind, leaf.value)
                } as Condition)
                return
              }
              if (leaf.kind === 'live') {
                const kind = liveValueKind(nextType, leaf.value)
                onChange({
                  ...leaf,
                  tag_id: tagId,
                  tag_path: tagPath,
                  cmp: coerceCmpForKind(leaf.cmp, kind),
                  value: defaultValueForKind(kind, leaf.value)
                } as Condition)
                return
              }
              onChange({ ...leaf, tag_id: tagId, tag_path: tagPath } as Condition)
            }}
          />
          {isUnresolvedTagRef(leaf) && (
            <p className="m-0 text-xs text-st-down">{UNRESOLVED_TAG_HINT}</p>
          )}
        </div>

        {leaf.kind === 'agg' && (
          <div className="flex flex-row gap-2">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label className="text-xs">Aggregate</Label>
              <Select
                value={leaf.fn}
                onValueChange={(val) =>
                  val && onChange({ ...(leaf as AggConditionLeaf), fn: val as ConditionFn })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONDITION_FNS.map((f) => (
                    <SelectItem key={f} value={f}>
                      {FN_LABELS[f]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label className="text-xs">Over</Label>
              <Select
                value={leaf.window}
                onValueChange={(val) =>
                  val &&
                  onChange({
                    ...(leaf as AggConditionLeaf),
                    window: val as ConditionWindow
                  })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {windowOptions(leaf.window).map((w) => (
                    <SelectItem key={w.value} value={w.value}>
                      {w.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {leaf.kind === 'deviation' && (
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Over</Label>
            <Select
              value={leaf.window}
              onValueChange={(val) =>
                val &&
                onChange({
                  ...(leaf as DeviationConditionLeaf),
                  window: val as ConditionWindow
                })
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {windowOptions(leaf.window).map((w) => (
                  <SelectItem key={w.value} value={w.value}>
                    {w.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {leaf.kind === 'alarm_active' ? (
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Alarm name</Label>
            <Input
              value={leaf.alarm_name}
              onChange={(e) =>
                onChange({
                  ...(leaf as AlarmActiveConditionLeaf),
                  alarm_name: e.target.value
                })
              }
              placeholder="High Pressure"
              className="h-8 text-xs"
            />
          </div>
        ) : (
          <div className="flex flex-row gap-2">
            <div className="flex flex-[2] flex-col gap-1.5">
              <Label className="text-xs">Comparison</Label>
              <Select
                value={leaf.cmp}
                onValueChange={(val) => {
                  if (!val) return
                  const next = { ...leaf, cmp: val as ConditionCmp } as Condition
                  if (!conditionSupportsDeadband(next) && 'deadband' in next) {
                    delete next.deadband
                  }
                  onChange(next)
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(leaf.kind === 'live'
                    ? cmpsForLiveKind(liveValueKind(tagType, leaf.value))
                    : CONDITION_CMPS
                  ).map((c) => (
                    <SelectItem key={c} value={c}>
                      {CMP_LABELS[c]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label className="text-xs">Value</Label>
              {leaf.kind === 'live' && liveValueKind(tagType, leaf.value) === 'boolean' ? (
                <Select
                  value={String(leaf.value)}
                  onValueChange={(val) =>
                    val &&
                    onChange({
                      ...(leaf as LiveConditionLeaf),
                      value: val === 'true'
                    } as Condition)
                  }
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">true</SelectItem>
                    <SelectItem value="false">false</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={
                    leaf.kind === 'live' && liveValueKind(tagType, leaf.value) === 'string'
                      ? 'text'
                      : 'number'
                  }
                  value={String(leaf.value)}
                  onChange={(e) => {
                    if (leaf.kind !== 'live') {
                      onChange({
                        ...leaf,
                        value: Number(e.target.value)
                      })
                      return
                    }
                    const currentKind = liveValueKind(tagType, leaf.value)
                    const value =
                      currentKind === 'string' ? e.target.value : parseLiveInput(e.target.value)
                    const kind = liveValueKind(tagType, value)
                    onChange({
                      ...leaf,
                      value,
                      cmp: coerceCmpForKind(leaf.cmp, kind)
                    } as Condition)
                  }}
                  className="h-8 text-xs"
                />
              )}
            </div>
          </div>
        )}

        {isValueLeaf(leaf) && conditionSupportsDeadband(leaf) && (
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Deadband</Label>
            <Input
              type="number"
              min={0}
              step="any"
              value={leaf.deadband ?? ''}
              onChange={(e) =>
                onChange({
                  ...leaf,
                  deadband: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value))
                } as Condition)
              }
              placeholder="Optional hysteresis width"
              className="h-8 text-xs"
            />
            <p className="m-0 text-xs text-ink-4">
              Prevents chatter near the threshold; applies only to numeric ordered comparisons.
            </p>
          </div>
        )}
      </div>

      {lockedToLive && (
        <p className="m-0 text-xs text-st-warn">
          Aggregates are only valid on numeric tags. This will save as a live comparison unless you
          pick a numeric tag.
        </p>
      )}

      <DescriptionField node={leaf} onChange={onChange} />
    </div>
  )
}

function ExprLeafEditor({
  leaf,
  onChange,
  onRemove,
  availableTagIds
}: {
  leaf: ExprConditionLeaf
  onChange: (next: Condition) => void
  onRemove?: () => void
  availableTagIds?: readonly string[]
  tagTypesById?: Record<string, string | undefined>
}) {
  const aliases = Object.keys(leaf.sources)
  const updateSource = (alias: string, patch: Partial<ExprConditionSource>) => {
    onChange({
      ...leaf,
      sources: { ...leaf.sources, [alias]: { ...leaf.sources[alias], ...patch } }
    })
  }
  const renameAlias = (from: string, to: string) => {
    const next = to.trim()
    if (!next || next === from || leaf.sources[next]) return
    const sources: Record<string, ExprConditionSource> = {}
    for (const [alias, src] of Object.entries(leaf.sources)) {
      sources[alias === from ? next : alias] = src
    }
    onChange({ ...leaf, sources })
  }
  const removeSource = (alias: string) => {
    if (aliases.length <= 1) return
    const { [alias]: _, ...rest } = leaf.sources
    onChange({ ...leaf, sources: rest })
  }
  const addSource = () => {
    if (!canAddExprSource(leaf.sources)) return
    const alias = nextExprAlias(aliases)
    onChange({
      ...leaf,
      sources: { ...leaf.sources, [alias]: { tag_id: '' } }
    })
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-bg-elev/40 p-3">
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="flex flex-row items-center gap-2">
          <span className="text-xs font-medium text-ink-3">When</span>
          <Select
            value={leaf.kind}
            onValueChange={(val) => {
              if (
                val === 'live' ||
                val === 'agg' ||
                val === 'deviation' ||
                val === 'alarm_active' ||
                val === 'expr'
              ) {
                onChange(defaultLeaf(val))
              }
            }}
          >
            <SelectTrigger className="h-7 w-36 px-2 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="live">{LEAF_KIND_LABELS.live}</SelectItem>
              <SelectItem value="agg">{LEAF_KIND_LABELS.agg}</SelectItem>
              <SelectItem value="deviation">{LEAF_KIND_LABELS.deviation}</SelectItem>
              <SelectItem value="alarm_active">{LEAF_KIND_LABELS.alarm_active}</SelectItem>
              <SelectItem value="expr">{LEAF_KIND_LABELS.expr}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onRemove}
            aria-label="Remove condition"
          >
            <XIcon className="size-4" />
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">Expression</Label>
        <Input
          value={leaf.expression}
          onChange={(e) => onChange({ ...leaf, expression: e.target.value })}
          placeholder="a + b"
          className="h-8 font-mono text-xs"
        />
        <p className="m-0 text-xs text-ink-4">
          Numeric formula using the source aliases below. Put the comparison in the fields under the
          sources, not in the formula.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label className="text-xs">Sources</Label>
        {aliases.map((alias) => {
          const src = leaf.sources[alias]
          const fn: ExprSourceFn = src.fn ?? 'last'
          const windowRequired = fn !== 'last'
          const aliasValid = EXPR_ALIAS_RE.test(alias)
          return (
            <div
              key={alias}
              className="flex flex-col gap-2 rounded-md border border-line bg-bg p-2"
            >
              <div className="flex flex-row items-end gap-2">
                <div className="flex w-24 flex-col gap-1.5">
                  <Label className="text-xs">Alias</Label>
                  <Input
                    value={alias}
                    onChange={(e) => renameAlias(alias, e.target.value)}
                    className="h-8 font-mono text-xs"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <Label className="text-xs">Tag</Label>
                  <RegistryTagPicker
                    value={src.tag_id}
                    selectedTagPath={src.tag_path}
                    availableTagIds={availableTagIds}
                    invalid={isUnresolvedTagRef(src)}
                    onChange={(tagId, tagPath) =>
                      updateSource(alias, { tag_id: tagId, tag_path: tagPath })
                    }
                  />
                  {isUnresolvedTagRef(src) && (
                    <p className="m-0 text-xs text-st-down">{UNRESOLVED_TAG_HINT}</p>
                  )}
                </div>
                {aliases.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => removeSource(alias)}
                    aria-label={`Remove source ${alias}`}
                  >
                    <XIcon className="size-4" />
                  </Button>
                )}
              </div>
              {!aliasValid && (
                <p className="m-0 text-xs text-st-down">Alias must match [a-z][a-z0-9_]*</p>
              )}
              <div className="flex flex-row gap-2">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label className="text-xs">Reduce as</Label>
                  <Select
                    value={fn}
                    onValueChange={(val) => {
                      if (!val) return
                      const nextFn = val as ExprSourceFn
                      updateSource(alias, {
                        fn: nextFn === 'last' ? undefined : nextFn,
                        window: nextFn === 'last' ? undefined : (src.window ?? '5m')
                      })
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPR_SOURCE_FNS.map((f) => (
                        <SelectItem key={f} value={f}>
                          {FN_LABELS[f]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {windowRequired && (
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Label className="text-xs">Over</Label>
                    <Select
                      value={(src.window as ConditionWindow | undefined) ?? '5m'}
                      onValueChange={(val) =>
                        val && updateSource(alias, { window: val as ConditionWindow })
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITION_WINDOWS.map((w) => (
                          <SelectItem key={w} value={w}>
                            {WINDOW_LABELS[w]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          )
        })}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start text-xs"
          disabled={!canAddExprSource(leaf.sources)}
          onClick={addSource}
        >
          Add source
        </Button>
      </div>

      <div className="flex flex-row gap-2">
        <div className="flex flex-[2] flex-col gap-1.5">
          <Label className="text-xs">Comparison</Label>
          <Select
            value={leaf.cmp}
            onValueChange={(val) => {
              if (!val) return
              const next: ExprConditionLeaf = { ...leaf, cmp: val as ConditionCmp }
              if (val === 'eq' || val === 'ne') {
                delete next.deadband
              }
              onChange(next)
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CONDITION_CMPS.map((c) => (
                <SelectItem key={c} value={c}>
                  {CMP_LABELS[c]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label className="text-xs">Value</Label>
          <Input
            type="number"
            step="any"
            value={String(leaf.value)}
            onChange={(e) => onChange({ ...leaf, value: Number(e.target.value) })}
            className="h-8 text-xs"
          />
        </div>
      </div>

      {conditionSupportsDeadband(leaf) && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">Deadband (optional)</Label>
          <Input
            type="number"
            min={0}
            step="any"
            value={leaf.deadband === undefined ? '' : String(leaf.deadband)}
            onChange={(e) => {
              const raw = e.target.value
              if (raw === '') {
                const next = { ...leaf }
                delete next.deadband
                onChange(next)
                return
              }
              onChange({ ...leaf, deadband: Number(raw) })
            }}
            placeholder="Optional hysteresis width"
            className="h-8 text-xs"
          />
        </div>
      )}

      <DescriptionField node={leaf} onChange={onChange} />
    </div>
  )
}

export { defaultConditionRoot } from './condition-defaults'

// Keep local defaultAnd for the builder; defaultConditionRoot lives in
// condition-defaults so every trigger editor shares one root.
